/** The awards ceremony: everyone's awards on one grid. Tap a card to open that person's. */

import { useEffect, useLayoutEffect, useRef, useState, type SyntheticEvent } from "react";

import { AWARD_CAPTIONS } from "../../copy";
import { Avatar, Fit, Scene, Zone, useColourOf, useStageExtra, type BlobSpec } from "../../story/stage";
import { useTimeline } from "../../story/timeline";
import { wr } from "../../theme";
import { AWARD_IDS, type Chat } from "../../types";

type Person = Chat["ceremony"][number];
type Open = { person: Person; opener: HTMLElement; from: number };

const BLOBS: BlobSpec[] = [{ colour: "surface-2", x: 980, y: 260, r: 200, seed: 20.3, wob: 0.2 }];

const TOP = 390;
// Down to just above the phone's arrows, on the designed 1920 stage.
const ROOM = 1780 - TOP;
const WIDE = 936;
const GAP = 24;
// The video's two-column card.
const CARD_W = 456;
const CARD_H = 400;

// The story moves on when its card is tapped; taps in here must not count as that.
const keep = (event: SyntheticEvent) => event.stopPropagation();

/**
 * How the grid fits the stage: the video's two columns while they fit, then
 * more columns and a smaller scale. Cards never get narrower than the video's
 * in their own pixels, so their text keeps its shape and just shrinks.
 */
export function gridPlan(cells: number, room = ROOM) {
  let best = { cols: 2, scale: 0 };
  for (let cols = 2; cols <= 6; cols++) {
    const rows = Math.ceil(cells / cols);
    const byWidth = WIDE / (CARD_W * cols + GAP * (cols - 1));
    const byHeight = room / (CARD_H * rows + GAP * (rows - 1));
    const scale = Math.min(1, byWidth, byHeight);
    if (scale > best.scale + 0.001) best = { cols, scale };
  }
  return best;
}

/** How tall a planned grid stands on the stage. */
function gridHeight(cells: number, { cols, scale }: { cols: number; scale: number }) {
  const rows = Math.ceil(cells / cols);
  return (CARD_H * rows + GAP * (rows - 1)) * scale;
}

export function Ceremony({ ceremony, me }: { ceremony: Person[]; me: string | null }) {
  const colourOf = useColourOf();
  const [open, setOpen] = useState<Open | null>(null);
  const grid = useRef<HTMLDivElement>(null);
  const extra = useStageExtra();
  const cells = ceremony.length + 1;
  // A taller stage gives a big chat's grid more room to grow into. Whatever
  // room the grid leaves beyond what it leaves at 1920 goes half above it.
  const room = ROOM + extra;
  const plan = gridPlan(cells, room);
  const spare = room - gridHeight(cells, plan) - (ROOM - gridHeight(cells, gridPlan(cells)));
  const drop = Math.max(0, spare / 2);
  // A new stage height starts again from the plan.
  const [fitted, setFitted] = useState({ room, shrink: 1 });
  const shrink = fitted.room === room ? fitted.shrink : 1;
  const scale = plan.scale * shrink;

  // Long stats can make a card taller than planned: shrink a little more until the grid fits.
  useLayoutEffect(() => {
    const el = grid.current;
    if (!el) return;
    const tall = el.offsetHeight * scale;
    if (tall > room - drop + 1 && shrink > 0.2) setFitted({ room, shrink: shrink * ((room - drop) / tall) });
  }, [room, drop, scale, shrink]);

  const given = ceremony.flatMap((p) => p.awards);
  // Fallback awards can go to several people; the hat counts each award once.
  const handed = new Set(given.map((a) => a.id)).size;
  const people = ceremony.length;
  const lead = [
    `${given.length} award${given.length === 1 ? "" : "s"}`,
    `${people} ${people === 1 ? "person" : "people"}`,
    ...(given.length > 1 && handed === given.length ? ["no repeats"] : []),
  ].join(", ");

  const root = useTimeline((s) => {
    s.rise(".kick, .lead, [data-part=hint]", 0.35, { y: 30, stagger: 0.12 });
    const cards = s.q(".cgrid > .ccard");
    // A big chat's cards arrive faster, so the last one isn't left waiting.
    const step = Math.min(0.12, 1.2 / Math.max(cards.length, 1));
    s.tl.fromTo(
      cards,
      { y: 120, autoAlpha: 0, rotation: (i: number) => (i % 2 ? 3 : -3) },
      { y: 0, autoAlpha: 1, rotation: 0, duration: 0.6, stagger: step, ease: "back.out(1.5)" },
      0.5,
    );
    cards.forEach((card, i) => {
      const awards = card.querySelectorAll(".aw");
      if (awards.length) s.tl.fromTo(awards, { x: -40, autoAlpha: 0 }, { x: 0, autoAlpha: 1, duration: 0.4, stagger: 0.12 }, 0.85 + i * step);
      if (i < 8) s.sound("pop", 0.5 + i * step, 1 + i * 0.06);
    });
  });

  return (
    <div ref={root} className="contents">
      <Scene blobs={BLOBS}>
        <Zone at="top">
          <div className="kick" style={{ color: wr("yellow") }}>
            The awards ceremony
          </div>
          <div className="lead">{lead}</div>
          <div
            data-part="hint"
            className="abs mono"
            style={{ right: 72, top: 196, fontSize: 32, fontWeight: 700, lineHeight: 1, letterSpacing: "0.06em", textTransform: "uppercase", color: wr("muted") }}
          >
            Tap a card
          </div>
        </Zone>
        {/* Between the top and the middle: the grid moves down only by the room it leaves. */}
        <Zone at={extra ? drop / extra : 0}>
          <div
            ref={grid}
            className="cgrid"
            style={{
              gridTemplateColumns: `repeat(${plan.cols}, 1fr)`,
              width: WIDE / scale,
              transform: scale < 1 ? `scale(${scale})` : undefined,
              transformOrigin: "0 0",
            }}
          >
            {ceremony.map((person, i) => {
              const mine = person.name === me;
              return (
                <button
                  key={person.name}
                  type="button"
                  data-testid="winner"
                  aria-haspopup="dialog"
                  className={`ccard${mine ? " me" : ""}`}
                  style={{ display: "block", width: "100%", textAlign: "left" }}
                  onPointerDown={keep}
                  onClick={(event) => {
                    keep(event);
                    const centre = 72 + ((i % plan.cols) + 0.5) * (WIDE / plan.cols);
                    setOpen({ person, opener: event.currentTarget, from: (centre - 540) * 0.55 });
                  }}
                >
                  <header>
                    <Avatar name={person.name} inline />
                    <b style={{ flex: "1 1 0", minWidth: 0 }}>
                      <Fit size={46}>{person.name}</Fit>
                    </b>
                    {mine ? <span>YOU</span> : null}
                  </header>
                  {person.awards.map((award) => (
                    <div key={award.id} className="aw">
                      <b style={{ color: colourOf(person.name) }}>
                        <Fit size={42}>{award.name}</Fit>
                      </b>
                      <span>{award.stat}</span>
                    </div>
                  ))}
                </button>
              );
            })}
            <div className="ccard end">
              <div className="disp">{AWARD_IDS.length}</div>
              <p>awards in the hat. {handed} handed out.</p>
            </div>
          </div>
        </Zone>
        {open ? <Sheet {...open} mine={open.person.name === me} onClose={() => setOpen(null)} /> : null}
      </Scene>
    </div>
  );
}

/** One person's awards over the dimmed grid, with their personality type. */
function Sheet({ person, opener, from, mine, onClose }: Open & { mine: boolean; onClose: () => void }) {
  const back = useRef<HTMLButtonElement>(null);
  const title = mine ? "Your awards" : `${person.name}'s awards`;
  const typeTop = 500 + person.awards.length * 440 + (person.awards.length ? 20 : 0);

  const root = useTimeline<HTMLDivElement>((s) => {
    s.press(opener, 0);
    s.tl.fromTo(".dim", { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3, ease: "none" }, 0.1);
    s.rise(".ohead, [data-part=back]", 0.2, { y: 30, sound: false });
    const cards = s.q(".ocard");
    if (cards.length) {
      s.tl.fromTo(
        cards,
        { x: from, y: -300, scale: 0.3, autoAlpha: 0 },
        { x: 0, y: 0, scale: 1, autoAlpha: 1, duration: 0.6, stagger: 0.18, ease: "back.out(1.3)" },
        0.25,
      );
      s.sound("whoosh", 0.25, 1.2, { length: 0.5 });
    }
    s.rise("[data-part=type]", 0.55 + cards.length * 0.18, { y: 30 });
  });

  // Focus moves into the sheet and back to the card it came from.
  useEffect(() => {
    back.current?.focus();
    return () => opener.focus();
  }, [opener]);

  // Escape closes the sheet, not the whole story: catch it before the player does.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  return (
    <div
      ref={root}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      tabIndex={-1}
      className="abs"
      style={{ inset: 0, outline: "none" }}
      onPointerDown={keep}
      onClick={keep}
      onKeyDown={(event) => {
        // Back is the only control in here, so Tab stays on it.
        if (event.key === "Tab") event.preventDefault();
      }}
    >
      {/* Outside the zones, so the dim always covers the whole stage. */}
      <div className="dim" onClick={onClose} />
      <Zone at="top">
        <button
          ref={back}
          type="button"
          data-part="back"
          className="abs mono"
          style={{
            left: 110,
            top: 196,
            padding: "18px 30px",
            borderRadius: 999,
            border: "4px solid currentColor",
            background: wr("ink"),
            fontSize: 30,
            fontWeight: 700,
            lineHeight: 1,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
          onClick={onClose}
        >
          <span aria-hidden>← </span>Back
        </button>
        <div className="ohead" style={{ right: 110 }}>
          <Avatar name={person.name} inline />
          <b style={{ flex: "1 1 0", minWidth: 0 }}>
            <Fit size={76}>{title}</Fit>
          </b>
        </div>
      </Zone>
      <Zone at="middle">
        {person.awards.map((award, i) => (
          <div key={award.id} className="ocard" style={{ top: 500 + i * 440 }}>
            <Fit size={112} className="disp">
              {award.name}
            </Fit>
            <div className="st">{award.stat}</div>
            {AWARD_CAPTIONS[award.id] ? <div className="cp">{AWARD_CAPTIONS[award.id]}</div> : null}
          </div>
        ))}
        <div data-part="type" className="abs" style={{ left: 110, right: 110, top: typeTop }}>
          <div className="mono" style={{ fontSize: 32, fontWeight: 700, lineHeight: 1, letterSpacing: "0.06em", textTransform: "uppercase", color: wr("yellow") }}>
            Personality
          </div>
          <Fit size={110} className="disp" style={{ marginTop: 22 }}>
            {person.personality}
          </Fit>
        </div>
      </Zone>
    </div>
  );
}
